import { getLocale, translate } from '../../i18n/index.js';
import { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { getStripeClient } from '../../components/payment/stripe-client.js';
import type { AppliedPromo, AvailableSlot, Gym, MembershipPlan } from '@atomfit/shared';
import axios from 'axios';
import {
  BackLink,
  formatCurrency,
  formatTime,
  Spinner,
  TierPill,
} from '../../components/design/AtomFitUI.js';
import { StripePaymentForm } from '../../components/payment/StripePaymentForm.js';
import {
  MembershipPlanCards,
  membershipSelectionCopy,
} from '../../components/membership/MembershipPlanCards.js';
import { bookingIntentsApi } from '../../api/booking-intents.api.js';
import { bookingsApi, type CustomerCreditDashboard } from '../../api/bookings.api.js';
import { gymsApi } from '../../api/gyms.api.js';
import { membershipsApi, type MyMembership } from '../../api/memberships.api.js';
import { slotsApi } from '../../api/slots.api.js';
import { useBookingStore } from '../../store/booking.store.js';
import { useMembershipCheckoutStore } from '../../store/membership-checkout.store.js';
import { useAuthStore } from '../../store/auth.store.js';
import { useBooking } from '../../hooks/useBooking.js';
import { useStripeAppearance } from '../../hooks/useStripeAppearance.js';

const PARTNER_PRICE_IN_SMALLEST_UNIT = 14900;

export function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const stripeAppearance = useStripeAppearance();
  const bookingIntentToken = searchParams.get('bookingIntent');
  const debugProcessing =
    searchParams.get('debugProcessing') === '1' || location.pathname === '/checkout-debug';
  const {
    gymId,
    selectedSlot,
    selectedSlots,
    bookingId,
    bookingGroupId,
    bookingExpiresAt,
    stripeAccountId,
    clientSecret,
    customerSessionClientSecret,
    setClientSecret,
    setBookingId,
    setGymId,
    setSelectedSlots,
  } = useBookingStore();
  const stripePromise = getStripeClient(stripeAccountId);
  const account = useAuthStore((state) => state.account);
  const setTenantSlug = useAuthStore((state) => state.setTenantSlug);
  const setMembershipCheckoutSelection = useMembershipCheckoutStore((state) => state.setSelection);
  const { startBookingBatch, replaceBooking, resumePayment } = useBooking();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerCode, setOfferCode] = useState('');
  const [useCredit, setUseCredit] = useState(true);
  const [includePartner, setIncludePartner] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [appliedOfferCode, setAppliedOfferCode] = useState('');
  const [appliedSavePaymentMethod, setAppliedSavePaymentMethod] = useState(false);
  const [appliedUseCredit, setAppliedUseCredit] = useState(true);
  const [appliedIncludePartner, setAppliedIncludePartner] = useState(false);
  const [pricedBooking, setPricedBooking] = useState<{
    basePriceInSmallestUnit: number;
    priceInSmallestUnit: number;
    referralDiscountInSmallestUnit: number;
    promoDiscountInSmallestUnit: number;
    appliedPromo: AppliedPromo | null;
    creditAppliedInSmallestUnit: number;
    partnerPriceInSmallestUnit: number;
    currency: string;
  } | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);
  const [stripeElementsAttempt, setStripeElementsAttempt] = useState(0);
  const [hydratingBookingIntent, setHydratingBookingIntent] = useState(Boolean(bookingIntentToken));
  const [bookingIntentError, setBookingIntentError] = useState<string | null>(null);
  const [credits, setCredits] = useState<CustomerCreditDashboard | null>(null);
  const [gymTenantId, setGymTenantId] = useState<string | null>(null);
  const [gymTenantSlug, setGymTenantSlug] = useState<string | null>(null);
  const [gymDetails, setGymDetails] = useState<Pick<
    Gym,
    'id' | 'name' | 'tenantId' | 'tenant' | 'slotBufferMinutes'
  > | null>(null);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [myMemberships, setMyMemberships] = useState<MyMembership[]>([]);
  const [membershipPlansLoading, setMembershipPlansLoading] = useState(false);
  const [membershipOfferOpen, setMembershipOfferOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const debugSlot = debugProcessing ? getDebugCheckoutSlot() : null;
  const checkoutGymId = gymId ?? debugSlot?.gymId ?? null;
  const checkoutSlots =
    selectedSlots.length > 0
      ? selectedSlots
      : selectedSlot
        ? [selectedSlot]
        : debugSlot
          ? [debugSlot]
          : [];
  const checkoutSlot = checkoutSlots[0] ?? null;
  const checkoutDateCount = new Set(checkoutSlots.map((slot) => formatDate(slot.startTime))).size;
  const checkoutSlotRanges = groupAdjacentSlots(checkoutSlots, gymDetails?.slotBufferMinutes ?? 0);
  const reservationExpired = remainingMs !== null && remainingMs <= 0;
  const remainingLabel = remainingMs !== null ? formatCountdown(remainingMs) : null;
  const selectedSlotInPast = checkoutSlots.some(
    (slot) => new Date(slot.startTime).getTime() <= nowMs,
  );
  const activeMembershipForFranchise = gymTenantId
    ? (myMemberships.find(
        (membership) =>
          membership.tenantId === gymTenantId &&
          membership.status === 'ACTIVE' &&
          (!membership.expiresAt || new Date(membership.expiresAt).getTime() > nowMs),
      ) ?? null)
    : null;
  const checkoutSlotsPriceInSmallestUnit = checkoutSlots.reduce(
    (total, slot) => total + slot.priceInSmallestUnit,
    0,
  );
  const freePartnerSlots = includePartner
    ? Math.min(checkoutSlots.length, activeMembershipForFranchise?.remainingGuestPasses ?? 0)
    : 0;
  const freePartnerSlotsMessageKey =
    freePartnerSlots === 1
      ? 'pages.public.checkout.checkout.span.free_partner_slot_one'
      : freePartnerSlots >= 2 && freePartnerSlots <= 4
        ? 'pages.public.checkout.checkout.span.free_partner_slot_few'
        : 'pages.public.checkout.checkout.span.free_partner_slot_many';
  const estimatedPartnerPriceInSmallestUnit = includePartner
    ? Math.max(0, checkoutSlots.length - freePartnerSlots) * PARTNER_PRICE_IN_SMALLEST_UNIT
    : 0;
  const displayedPartnerPriceInSmallestUnit =
    pricedBooking?.partnerPriceInSmallestUnit ?? estimatedPartnerPriceInSmallestUnit;
  const estimatedSubtotalInSmallestUnit =
    checkoutSlotsPriceInSmallestUnit + estimatedPartnerPriceInSmallestUnit;
  const creditEstimate =
    !pricedBooking && checkoutSlot
      ? estimateApplicableCreditSplit(
          credits,
          gymTenantId,
          estimatedSubtotalInSmallestUnit,
          checkoutSlot.currency,
        )
      : {
          franchise: 0,
          global: 0,
          total: 0,
          franchiseName: translate('pages.public.checkout.credit_estimate.franchise_name'),
        };
  const estimatedCreditAppliedInSmallestUnit = useCredit ? creditEstimate.total : 0;
  const displayedCreditAppliedInSmallestUnit =
    useCredit || pricedBooking
      ? (pricedBooking?.creditAppliedInSmallestUnit ?? estimatedCreditAppliedInSmallestUnit)
      : 0;
  const displayedTotalInSmallestUnit =
    pricedBooking?.priceInSmallestUnit ??
    Math.max(0, estimatedSubtotalInSmallestUnit - estimatedCreditAppliedInSmallestUnit);
  const displayedCurrency = pricedBooking?.currency ?? checkoutSlot?.currency ?? 'CZK';
  const displayedMembershipDiscountInSmallestUnit = pricedBooking
    ? 0
    : checkoutSlots.reduce(
        (total, slot) =>
          total +
          Math.max(
            0,
            (slot.publicPriceInSmallestUnit ?? slot.priceInSmallestUnit) - slot.priceInSmallestUnit,
          ),
        0,
      );
  const creditCoversTotal = displayedTotalInSmallestUnit <= 0;
  const isCheckoutProcessing = loading || debugProcessing;
  const hasActiveMembershipForFranchise = Boolean(activeMembershipForFranchise);
  const canPrepareCheckout =
    Boolean(checkoutGymId && checkoutSlots.length > 0) &&
    Boolean(gymTenantSlug) &&
    !selectedSlotInPast &&
    !reservationExpired &&
    !clientSecret &&
    !isCheckoutProcessing;
  const paymentSetupCollapsed = isCheckoutProcessing || Boolean(clientSecret);

  useEffect(() => {
    if (!bookingIntentToken) {
      setHydratingBookingIntent(false);
      return;
    }

    let cancelled = false;
    setHydratingBookingIntent(true);
    setBookingIntentError(null);

    bookingIntentsApi
      .get(bookingIntentToken)
      .then((response) => {
        if (cancelled) return;
        setGymId(response.data.gymId);
        setTenantSlug(response.data.tenant.slug);
        setSelectedSlots(response.data.slots?.length ? response.data.slots : [response.data.slot]);
        if (response.data.offerCode) {
          setOfferCode(response.data.offerCode);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBookingIntentError(
            translate('pages.public.checkout.checkout.set_booking_intent_error.error_booking_slot'),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setHydratingBookingIntent(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bookingIntentToken, setGymId, setSelectedSlots, setTenantSlug]);

  useEffect(() => {
    if (!gymId || !account) {
      setCredits(null);
      setGymTenantId(null);
      setGymTenantSlug(null);
      setGymDetails(null);
      return;
    }

    let cancelled = false;
    gymsApi
      .get(gymId)
      .then((gymResponse) => {
        if (cancelled) return;
        const tenantSlug = gymResponse.data.tenant?.slug ?? null;
        setGymTenantId(gymResponse.data.tenantId);
        setGymTenantSlug(tenantSlug);
        setGymDetails({
          id: gymResponse.data.id,
          name: gymResponse.data.name,
          tenantId: gymResponse.data.tenantId,
          ...(gymResponse.data.tenant ? { tenant: gymResponse.data.tenant } : {}),
          slotBufferMinutes: gymResponse.data.slotBufferMinutes,
        });
        if (tenantSlug) setTenantSlug(tenantSlug);
      })
      .catch(() => {
        if (!cancelled) {
          setGymTenantId(null);
          setGymTenantSlug(null);
          setGymDetails(null);
        }
      });

    bookingsApi
      .myCredits()
      .then((creditResponse) => {
        if (!cancelled) setCredits(creditResponse.data);
      })
      .catch(() => {
        if (!cancelled) setCredits(null);
      });

    return () => {
      cancelled = true;
    };
  }, [account, gymId, setTenantSlug]);

  useEffect(() => {
    if (!account) {
      setMyMemberships([]);
      return;
    }

    let cancelled = false;
    membershipsApi
      .my()
      .then((response) => {
        if (!cancelled) setMyMemberships(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setMyMemberships([]);
      });

    return () => {
      cancelled = true;
    };
  }, [account]);

  useEffect(() => {
    if (!gymTenantId || checkoutSlots.length === 0 || pricedBooking) return;

    const membership = myMemberships.find(
      (item) =>
        item.tenantId === gymTenantId &&
        item.status === 'ACTIVE' &&
        (!item.expiresAt || new Date(item.expiresAt).getTime() > nowMs),
    );
    if (!membership) return;

    const nextSlots = checkoutSlots.map((slot) => applyMembershipPrice(slot, membership));
    const hasPriceChange = nextSlots.some((slot, index) => {
      const current = checkoutSlots[index]!;
      return (
        slot.priceInSmallestUnit !== current.priceInSmallestUnit ||
        slot.publicPriceInSmallestUnit !== current.publicPriceInSmallestUnit ||
        slot.membershipPlanName !== current.membershipPlanName ||
        slot.membershipDiscountPercent !== current.membershipDiscountPercent
      );
    });

    if (hasPriceChange) setSelectedSlots(nextSlots);
  }, [checkoutSlots, gymTenantId, myMemberships, nowMs, pricedBooking, setSelectedSlots]);

  useEffect(() => {
    if (
      !account ||
      !gymId ||
      checkoutSlots.length === 0 ||
      clientSecret ||
      isCheckoutProcessing ||
      pricedBooking
    ) {
      return;
    }

    const controller = new AbortController();
    const selectedTimeKeys = new Set(
      checkoutSlots.map((slot) => new Date(slot.startTime).getTime()),
    );
    const dates = Array.from(
      new Set(checkoutSlots.map((slot) => formatInputDateLocal(slot.startTime))),
    );

    Promise.all(dates.map((date) => slotsApi.list(gymId, date, controller.signal)))
      .then((responses) => {
        const refreshedSlots = responses.flatMap((response) => response.data.data);
        const refreshedByTime = new Map(
          refreshedSlots
            .filter((slot) => selectedTimeKeys.has(new Date(slot.startTime).getTime()))
            .map((slot) => [new Date(slot.startTime).getTime(), slot]),
        );
        if (refreshedByTime.size === 0) return;

        const nextSlots = checkoutSlots.map((slot) => {
          const refreshed = refreshedByTime.get(new Date(slot.startTime).getTime());
          return refreshed ?? slot;
        });
        const hasPriceChange = nextSlots.some((slot, index) => {
          const current = checkoutSlots[index]!;
          return (
            slot.priceInSmallestUnit !== current.priceInSmallestUnit ||
            slot.publicPriceInSmallestUnit !== current.publicPriceInSmallestUnit ||
            slot.membershipPlanName !== current.membershipPlanName ||
            slot.membershipDiscountPercent !== current.membershipDiscountPercent
          );
        });

        if (hasPriceChange) setSelectedSlots(nextSlots);
      })
      .catch((err: unknown) => {
        if (!axios.isCancel(err)) {
          // Keep the existing selected slots if the refresh fails; booking creation still reprices server-side.
        }
      });

    return () => controller.abort();
  }, [
    account,
    checkoutSlots,
    clientSecret,
    gymId,
    isCheckoutProcessing,
    pricedBooking,
    setSelectedSlots,
  ]);

  useEffect(() => {
    if (
      !checkoutGymId ||
      paymentSetupCollapsed ||
      reservationExpired ||
      selectedSlotInPast ||
      hasActiveMembershipForFranchise
    ) {
      setMembershipPlans([]);
      setMembershipPlansLoading(false);
      setMembershipOfferOpen(false);
      return;
    }

    let cancelled = false;
    setMembershipPlansLoading(true);
    membershipsApi
      .plansByGym(checkoutGymId)
      .then((response) => {
        if (!cancelled) setMembershipPlans(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setMembershipPlans([]);
      })
      .finally(() => {
        if (!cancelled) setMembershipPlansLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    checkoutGymId,
    hasActiveMembershipForFranchise,
    paymentSetupCollapsed,
    reservationExpired,
    selectedSlotInPast,
  ]);

  function prepareCheckout() {
    if (
      !gymId ||
      checkoutSlots.length === 0 ||
      selectedSlotInPast ||
      clientSecret ||
      isCheckoutProcessing
    )
      return;
    const normalizedOfferCode = offerCode.trim().toUpperCase();
    setLoading(true);
    setError(null);

    if (bookingId) {
      resumePayment(bookingId, bookingGroupId, savePaymentMethod)
        .then(({ clientSecret: secret }) => {
          setAppliedOfferCode(normalizedOfferCode);
          setAppliedSavePaymentMethod(savePaymentMethod);
          setAppliedUseCredit(useCredit);
          setAppliedIncludePartner(includePartner);
          setClientSecret(secret);
        })
        .catch(handleCheckoutError)
        .finally(() => setLoading(false));
      return;
    }

    startBookingBatch(
      gymId,
      checkoutSlots.map((slot) => new Date(slot.startTime).toISOString()),
      normalizedOfferCode || undefined,
      savePaymentMethod,
      useCredit,
      includePartner,
    )
      .then((bookingResult) => {
        const { bookingId, clientSecret: secret } = bookingResult;
        setPricedBooking(bookingResult);
        setBookingId(bookingId);
        setAppliedOfferCode(normalizedOfferCode);
        setAppliedSavePaymentMethod(savePaymentMethod);
        setAppliedUseCredit(useCredit);
        setAppliedIncludePartner(includePartner);
        if (!secret) {
          void navigate(`/booking-confirmed?bookingId=${encodeURIComponent(bookingId)}`);
          return;
        }
        setClientSecret(secret);
      })
      .catch(handleCheckoutError)
      .finally(() => setLoading(false));
  }

  function selectMembershipPlan(plan: MembershipPlan) {
    if (!checkoutGymId || !gymTenantId) return;

    setMembershipCheckoutSelection({
      gymId: checkoutGymId,
      ...(gymDetails?.tenant?.name ? { tenantName: gymDetails.tenant.name } : {}),
      tenantId: gymTenantId,
      plan,
      returnTo: 'checkout',
    });

    void navigate('/membership-checkout');
  }

  useEffect(() => {
    if (
      !gymId ||
      !selectedSlot ||
      selectedSlots.length > 1 ||
      !bookingId ||
      !clientSecret ||
      isCheckoutProcessing ||
      reservationExpired ||
      selectedSlotInPast
    ) {
      return;
    }

    const normalizedOfferCode = offerCode.trim().toUpperCase();
    if (
      normalizedOfferCode === appliedOfferCode &&
      savePaymentMethod === appliedSavePaymentMethod &&
      useCredit === appliedUseCredit &&
      includePartner === appliedIncludePartner
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      setClientSecret(null);

      replaceBooking(
        bookingId,
        gymId,
        new Date(selectedSlot.startTime).toISOString(),
        normalizedOfferCode || undefined,
        savePaymentMethod,
        useCredit,
        includePartner,
      )
        .then((bookingResult) => {
          const { bookingId: nextBookingId, clientSecret: secret } = bookingResult;
          setPricedBooking(bookingResult);
          setBookingId(nextBookingId);
          setAppliedOfferCode(normalizedOfferCode);
          setAppliedSavePaymentMethod(savePaymentMethod);
          setAppliedUseCredit(useCredit);
          setAppliedIncludePartner(includePartner);
          if (!secret) {
            void navigate(`/booking-confirmed?bookingId=${encodeURIComponent(nextBookingId)}`);
            return;
          }
          setClientSecret(secret);
        })
        .catch(handleCheckoutError)
        .finally(() => setLoading(false));
    }, 650);

    return () => window.clearTimeout(timeoutId);
  }, [
    appliedOfferCode,
    appliedIncludePartner,
    appliedSavePaymentMethod,
    appliedUseCredit,
    bookingId,
    clientSecret,
    gymId,
    includePartner,
    isCheckoutProcessing,
    navigate,
    offerCode,
    replaceBooking,
    reservationExpired,
    savePaymentMethod,
    selectedSlot,
    selectedSlots.length,
    selectedSlotInPast,
    setBookingId,
    setClientSecret,
    useCredit,
  ]);

  function handleCheckoutError(err: unknown) {
    if (isPasswordRequiredError(err)) {
      const returnTo = `${location.pathname}${location.search}`;
      void navigate(`/auth/create-password?redirect=${encodeURIComponent(returnTo)}`, {
        replace: true,
      });
      return;
    }
    setError(getCheckoutErrorMessage(err));
  }

  useEffect(() => {
    setStripeLoadError(null);
  }, [clientSecret]);

  async function reloadPaymentSession() {
    if (!bookingId) return;

    setLoading(true);
    setError(null);
    setStripeLoadError(null);

    try {
      await resumePayment(bookingId, bookingGroupId, savePaymentMethod);
      setStripeElementsAttempt((attempt) => attempt + 1);
    } catch (err) {
      handleCheckoutError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!bookingExpiresAt) {
      setRemainingMs(null);
      return;
    }

    const updateRemaining = () => {
      setRemainingMs(Math.max(0, new Date(bookingExpiresAt).getTime() - Date.now()));
    };

    updateRemaining();
    const intervalId = window.setInterval(updateRemaining, 1000);

    return () => window.clearInterval(intervalId);
  }, [bookingExpiresAt]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  if (!checkoutGymId || !checkoutSlot) {
    if (bookingIntentToken) {
      return (
        <main className="af-screen">
          <section className="af-shell af-booking-flow-shell" style={{ paddingTop: 80 }}>
            {hydratingBookingIntent && (
              <div
                className="af-card"
                style={{
                  padding: 28,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />
                {translate('pages.public.checkout.checkout.div_card.booking')}
              </div>
            )}
            {!hydratingBookingIntent && bookingIntentError && (
              <div className="af-card" style={{ padding: 22 }}>
                <div className="af-error" style={{ marginBottom: 16 }}>
                  {bookingIntentError}
                </div>
                <button
                  className="af-primary-btn"
                  type="button"
                  onClick={() => void navigate('/gyms')}
                  style={{ width: '100%', padding: 16, fontSize: 17 }}
                >
                  {translate('pages.public.checkout.checkout.button_primary_btn.slot')}
                </button>
              </div>
            )}
          </section>
        </main>
      );
    }
    return <Navigate to="/gyms" replace />;
  }

  return (
    <main className="af-screen af-checkout-screen">
      <section className="af-shell af-booking-flow-shell af-checkout-shell">
        <BackLink to={`/gyms/${checkoutGymId}`}>
          {translate('pages.public.checkout.checkout.back_link.slot_back')}
        </BackLink>
        <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px', margin: '0 0 6px' }}>
          {translate('pages.public.checkout.checkout.h1.booking')}
        </h1>
        <p style={{ color: 'var(--af-muted)', margin: '0 0 28px', fontSize: 15 }}>
          {translate('pages.public.checkout.checkout.p.training')}
        </p>

        <div className="af-card af-checkout-summary">
          <div
            style={{
              fontSize: 11,
              color: 'var(--af-muted)',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 14,
            }}
          >
            {translate('pages.public.checkout.checkout.div.summary')}
          </div>
          <SummaryRow
            label={translate('pages.public.checkout.checkout.summary_row.gym')}
            value={gymDetails?.name ?? 'AtomFit'}
          />

          <SummaryRow
            label={
              checkoutDateCount > 1
                ? translate('pages.public.checkout.checkout.summary_row.slot')
                : translate('pages.public.checkout.checkout.summary_row.date')
            }
            value={
              checkoutDateCount > 1
                ? `${checkoutDateCount} dny`
                : formatDate(checkoutSlot.startTime)
            }
          />

          {checkoutSlotRanges.map((range) => (
            <SummaryRow
              key={String(range.startTime)}
              label={translate('pages.public.checkout.checkout.summary_row.label')}
              value={
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    {checkoutDateCount > 1
                      ? translate('pages.public.checkout.checkout.summary_row.value', {
                          value1: formatDate(range.startTime),
                        })
                      : ''}
                    {formatTime(range.startTime)} {'-'} {formatTime(range.endTime)}
                  </span>
                  {range.pricingTiers.length === 1 && range.pricingTiers[0] !== 'STANDARD' && (
                    <TierPill tier={range.pricingTiers[0]!} />
                  )}
                </span>
              }
            />
          ))}
          {displayedMembershipDiscountInSmallestUnit > 0 && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.membership')}
              value={`-${formatCurrency(displayedMembershipDiscountInSmallestUnit, displayedCurrency)}`}
            />
          )}
          {includePartner && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.training')}
              value={
                displayedPartnerPriceInSmallestUnit > 0
                  ? formatCurrency(displayedPartnerPriceInSmallestUnit, displayedCurrency)
                  : 'Zdarma'
              }
            />
          )}
          {pricedBooking && pricedBooking.referralDiscountInSmallestUnit > 0 && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.referral')}
              value={`-${formatCurrency(pricedBooking.referralDiscountInSmallestUnit, pricedBooking.currency)}`}
            />
          )}
          {pricedBooking && pricedBooking.promoDiscountInSmallestUnit > 0 && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.promo_code', {
                value1: pricedBooking.appliedPromo ? ` (${pricedBooking.appliedPromo.code})` : '',
              })}
              value={`-${formatCurrency(pricedBooking.promoDiscountInSmallestUnit, pricedBooking.currency)}`}
            />
          )}
          {displayedCreditAppliedInSmallestUnit > 0 && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.credit_edit')}
              value={`-${formatCurrency(displayedCreditAppliedInSmallestUnit, displayedCurrency)}`}
            />
          )}
          {remainingLabel && !reservationExpired && (
            <SummaryRow
              label={translate('pages.public.checkout.checkout.summary_row.label_2')}
              value={
                <span
                  className="af-mono"
                  style={{ color: 'var(--af-accent)', fontSize: 18, fontWeight: 900 }}
                >
                  {remainingLabel}
                </span>
              }
            />
          )}
          <div
            style={{
              borderTop: '1px solid var(--af-border)',
              marginTop: 14,
              paddingTop: 14,
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 800,
              fontSize: 18,
            }}
          >
            <span>{translate('pages.public.checkout.checkout.span.total')}</span>
            <span style={{ color: 'var(--af-accent)' }}>
              {formatCurrency(displayedTotalInSmallestUnit, displayedCurrency)}
            </span>
          </div>
        </div>

        {!paymentSetupCollapsed &&
          !reservationExpired &&
          !selectedSlotInPast &&
          !hasActiveMembershipForFranchise && (
            <MembershipCheckoutOffer
              loading={membershipPlansLoading}
              onSelectPlan={selectMembershipPlan}
              open={membershipOfferOpen}
              plans={membershipPlans}
              setOpen={setMembershipOfferOpen}
            />
          )}

        {!isCheckoutProcessing && !clientSecret && !reservationExpired && selectedSlotInPast && (
          <div className="af-card" style={{ padding: 22 }}>
            <p style={{ margin: '0 0 16px', color: 'var(--af-muted)', lineHeight: 1.5 }}>
              {translate('pages.public.checkout.checkout.p.slot_select')}
            </p>
            <button
              className="af-primary-btn"
              type="button"
              onClick={() => void navigate(`/gyms/${checkoutGymId}`)}
              style={{ width: '100%', padding: 16, fontSize: 17 }}
            >
              {translate('pages.public.checkout.checkout.button_primary_btn.slot_2')}
            </button>
          </div>
        )}
        {!reservationExpired && !selectedSlotInPast && (
          <>
            {!paymentSetupCollapsed && (
              <div className="af-checkout-collapse">
                <div className="af-card af-checkout-options-card">
                  <form
                    className="af-checkout-promo-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      prepareCheckout();
                    }}
                  >
                    <label className="af-label" htmlFor="checkout-promo-code">
                      {translate('pages.public.checkout.checkout.label_label.promo_code')}

                      <input
                        id="checkout-promo-code"
                        className="af-input af-mono"
                        type="text"
                        value={offerCode}
                        onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
                        autoComplete="off"
                        disabled={paymentSetupCollapsed}
                      />
                    </label>
                    <button
                      className="af-primary-btn af-checkout-promo-submit"
                      type="submit"
                      disabled={!offerCode.trim() || !canPrepareCheckout}
                    >
                      {translate(
                        'pages.public.checkout.checkout.button_primary_btn.promo_code_apply',
                      )}
                    </button>
                  </form>
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      color: 'var(--af-muted)',
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.45,
                      marginTop: 16,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={includePartner}
                      onChange={(event) => {
                        setIncludePartner(event.target.checked);
                        if (!clientSecret) setPricedBooking(null);
                      }}
                      disabled={paymentSetupCollapsed}
                      style={{ marginTop: 3, accentColor: 'var(--af-cta)' }}
                    />

                    <span>
                      {translate('pages.public.checkout.checkout.span.training')}

                      <span style={{ display: 'block', color: 'var(--af-dim)', marginTop: 3 }}>
                        {activeMembershipForFranchise &&
                        activeMembershipForFranchise.plan.guestPassesPerMonth > 0
                          ? translate('pages.public.checkout.checkout.span.membership_slot', {
                              value1: activeMembershipForFranchise.plan.guestPassesPerMonth,
                              value2: formatCurrency(
                                PARTNER_PRICE_IN_SMALLEST_UNIT,
                                displayedCurrency,
                              ),
                            })
                          : translate('pages.public.checkout.checkout.span.slot', {
                              value1: formatCurrency(
                                PARTNER_PRICE_IN_SMALLEST_UNIT,
                                displayedCurrency,
                              ),
                            })}
                      </span>
                      {includePartner &&
                        activeMembershipForFranchise &&
                        activeMembershipForFranchise.plan.guestPassesPerMonth > 0 && (
                          <span
                            style={{ display: 'block', color: 'var(--af-accent)', marginTop: 5 }}
                          >
                            {translate(freePartnerSlotsMessageKey, { value1: freePartnerSlots })}
                          </span>
                        )}
                    </span>
                  </label>
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      color: 'var(--af-muted)',
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.45,
                      marginTop: 16,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={useCredit && creditEstimate.total > 0}
                      onChange={(event) => {
                        setUseCredit(event.target.checked);
                        if (!event.target.checked && !clientSecret) setPricedBooking(null);
                      }}
                      disabled={paymentSetupCollapsed || creditEstimate.total <= 0}
                      style={{ marginTop: 3, accentColor: 'var(--af-cta)' }}
                    />

                    <span>
                      {translate('pages.public.checkout.checkout.span.credit_edit')}

                      {creditEstimate.total > 0 && (
                        <span style={{ display: 'block', marginTop: 5 }}>
                          {creditEstimate.franchise > 0 && (
                            <span style={{ display: 'block', color: 'var(--af-accent)' }}>
                              {creditEstimate.franchiseName}
                              {': -'}
                              {formatCurrency(creditEstimate.franchise, displayedCurrency)}
                            </span>
                          )}
                          {creditEstimate.global > 0 && (
                            <span style={{ display: 'block', color: 'var(--af-accent)' }}>
                              {translate('pages.public.checkout.checkout.span.credit_edit_2')}

                              {formatCurrency(creditEstimate.global, displayedCurrency)}
                            </span>
                          )}
                        </span>
                      )}
                      {creditEstimate.total <= 0 && (
                        <span style={{ display: 'block', color: 'var(--af-dim)', marginTop: 3 }}>
                          {translate('pages.public.checkout.checkout.span.credit_edit_3')}
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {!paymentSetupCollapsed && !creditCoversTotal && (
              <div className="af-card af-checkout-payment-card">
                <div className="af-checkout-collapse">
                  <label
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      color: 'var(--af-muted)',
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={savePaymentMethod}
                      onChange={(event) => setSavePaymentMethod(event.target.checked)}
                      disabled={paymentSetupCollapsed}
                      style={{ marginTop: 3, accentColor: 'var(--af-cta)' }}
                    />

                    <span>
                      {translate('pages.public.checkout.checkout.span.payment_credit_save')}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {!clientSecret && (
              <div className="af-checkout-submit-row">
                <button
                  className={`af-primary-btn af-checkout-submit${isCheckoutProcessing ? ' is-processing' : ''}`}
                  onClick={prepareCheckout}
                  type="button"
                  disabled={!canPrepareCheckout}
                  style={{ padding: 16, fontSize: 17 }}
                >
                  <span className="af-processing-button-text">
                    {isCheckoutProcessing
                      ? creditCoversTotal
                        ? translate(
                            'pages.public.checkout.checkout.span_processing_button_text.booking',
                          )
                        : translate(
                            'pages.public.checkout.checkout.span_processing_button_text.copy',
                          )
                      : creditCoversTotal
                        ? translate(
                            'pages.public.checkout.checkout.span_processing_button_text.booking_2',
                          )
                        : translate(
                            'pages.public.checkout.checkout.span_processing_button_text.payment',
                          )}
                  </span>
                  {isCheckoutProcessing && (
                    <span className="af-processing-button-orbit" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                  )}
                </button>
              </div>
            )}

            {debugProcessing && (
              <div className="af-checkout-debug-note">
                {translate('pages.public.checkout.checkout.div_checkout_debug_note.booking')}
              </div>
            )}

            {clientSecret && (
              <div
                className={
                  !isCheckoutProcessing && !error && bookingId && stripePromise && !stripeLoadError
                    ? 'af-checkout-payment-shell'
                    : 'af-card af-checkout-payment-card'
                }
              >
                {!isCheckoutProcessing && !error && !stripePromise && (
                  <div className="af-error">
                    {translate('pages.public.checkout.checkout.div_error.payment_stripe')}
                  </div>
                )}

                {!isCheckoutProcessing && !error && stripePromise && stripeLoadError && (
                  <div>
                    <div className="af-error" style={{ marginBottom: 14 }}>
                      {translate('pages.public.checkout.checkout.div_error.error_payment')}
                      <div style={{ marginTop: 6, fontSize: 12 }}>{stripeLoadError}</div>
                    </div>
                    <button
                      className="af-primary-btn"
                      type="button"
                      onClick={() => void reloadPaymentSession()}
                      style={{ width: '100%', padding: 16 }}
                    >
                      {translate('pages.public.checkout.checkout.button_primary_btn.payment')}
                    </button>
                  </div>
                )}

                {!isCheckoutProcessing &&
                  !error &&
                  bookingId &&
                  stripePromise &&
                  !stripeLoadError && (
                    <Elements
                      key={`${stripeAccountId ?? 'platform'}:${stripeAppearance.key}:${stripeElementsAttempt}`}
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        locale: getLocale(),
                        ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
                        appearance: stripeAppearance.appearance,
                      }}
                    >
                      <StripePaymentForm
                        onLoadError={(message) => {
                          setStripeLoadError(message);
                        }}
                        onSuccess={() =>
                          void navigate(
                            `/booking-confirmed?bookingId=${encodeURIComponent(bookingId)}`,
                          )
                        }
                        returnUrl={`${window.location.origin}/booking-confirmed?bookingId=${encodeURIComponent(bookingId)}`}
                        customerEmail={account?.email}
                        savePaymentMethod={savePaymentMethod}
                        splitSubmit
                      />
                    </Elements>
                  )}
              </div>
            )}
          </>
        )}
        {error && (
          <div className="af-error">
            {translate('pages.public.checkout.checkout.div_error.error_payment_2')} {error}
          </div>
        )}
        {!isCheckoutProcessing && !error && reservationExpired && (
          <div className="af-card" style={{ padding: 22 }}>
            <p style={{ margin: '0 0 16px', color: 'var(--af-muted)', lineHeight: 1.5 }}>
              {translate('pages.public.checkout.checkout.p.booking_slot_create')}
            </p>
            <button
              className="af-primary-btn"
              type="button"
              onClick={() => void navigate(`/gyms/${checkoutGymId}`)}
              style={{ width: '100%', padding: 16, fontSize: 17 }}
            >
              {translate('pages.public.checkout.checkout.button_primary_btn.slot_create_2')}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

function MembershipCheckoutOffer({
  loading,
  onSelectPlan,
  open,
  plans,
  setOpen,
}: {
  loading: boolean;
  onSelectPlan: (plan: MembershipPlan) => void;
  open: boolean;
  plans: MembershipPlan[];
  setOpen: (open: boolean) => void;
}) {
  if (!loading && plans.length === 0) return null;

  return (
    <section
      className="af-card af-checkout-membership-offer"
      aria-labelledby="membership-offer-title"
    >
      <button
        className="af-checkout-membership-toggle"
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="membership-offer-plans"
      >
        <span>
          <span id="membership-offer-title" className="af-checkout-membership-title">
            {translate(
              'pages.public.checkout.membership_checkout_offer.span_checkout_membership_title.training',
            )}
          </span>
          <span className="af-checkout-membership-subtitle">
            {translate(
              'pages.public.checkout.membership_checkout_offer.span_checkout_membership_subtitle.select',
            )}
          </span>
        </span>
        <span className="af-checkout-membership-chevron" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>

      <div
        id="membership-offer-plans"
        className={`af-checkout-membership-panel${open ? ' is-open' : ''}`}
        aria-hidden={!open}
      >
        <div className="af-checkout-membership-panel-inner">
          <div className="af-membership-select-section">
            <div className="af-membership-select-intro">
              <h2>{membershipSelectionCopy.title}</h2>
              <p>{membershipSelectionCopy.description}</p>
            </div>
            {loading ? (
              <div className="af-checkout-membership-loading">
                <Spinner />
                {translate(
                  'pages.public.checkout.membership_checkout_offer.div_checkout_membership_loading.loading_membership',
                )}
              </div>
            ) : (
              <MembershipPlanCards
                plans={plans}
                onChoose={onSelectPlan}
                buttonTabIndex={open ? 0 : -1}
                showIncludedAmenities
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function applyMembershipPrice(slot: AvailableSlot, membership: MyMembership): AvailableSlot {
  const publicPriceInSmallestUnit = slot.publicPriceInSmallestUnit ?? slot.priceInSmallestUnit;
  const priceInSmallestUnit =
    membership.plan.slotPriceInSmallestUnit ??
    Math.max(
      0,
      Math.round((publicPriceInSmallestUnit * (100 - membership.plan.discountPercent)) / 100),
    );

  if (priceInSmallestUnit >= publicPriceInSmallestUnit) return slot;

  return {
    ...slot,
    priceInSmallestUnit,
    publicPriceInSmallestUnit,
    membershipDiscountPercent: membership.plan.discountPercent,
    membershipPlanName: membership.plan.name,
  };
}

function formatCountdown(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function groupAdjacentSlots(slots: AvailableSlot[], bufferMinutes: number) {
  const bufferMilliseconds = Math.max(0, bufferMinutes) * 60_000;
  const sortedSlots = [...slots].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  return sortedSlots.reduce<
    Array<{ startTime: Date; endTime: Date; pricingTiers: AvailableSlot['pricingTier'][] }>
  >((ranges, slot) => {
    const startTime = new Date(slot.startTime);
    const endTime = new Date(slot.endTime);
    const previousRange = ranges[ranges.length - 1];

    if (
      previousRange &&
      startTime.getTime() === previousRange.endTime.getTime() + bufferMilliseconds
    ) {
      previousRange.endTime = endTime;
      if (!previousRange.pricingTiers.includes(slot.pricingTier)) {
        previousRange.pricingTiers.push(slot.pricingTier);
      }
      return ranges;
    }

    ranges.push({ startTime, endTime, pricingTiers: [slot.pricingTier] });
    return ranges;
  }, []);
}

function formatInputDateLocal(value: Date | string) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDebugCheckoutSlot(): AvailableSlot {
  const start = new Date();
  start.setDate(start.getDate() + 2);
  start.setHours(7, 25, 0, 0);

  const end = new Date(start);
  end.setMinutes(end.getMinutes() + 30);

  return {
    gymId: 'debug-gym',
    startTime: start,
    endTime: end,
    priceInSmallestUnit: 0,
    currency: 'CZK',
    pricingTier: 'STANDARD',
    capacityTotal: 1,
    capacityBooked: 0,
    status: 'AVAILABLE',
    slotId: 'debug-slot',
  };
}

function getCheckoutErrorMessage(err: unknown) {
  if (!axios.isAxiosError(err)) {
    return err instanceof Error
      ? err.message
      : translate('pages.public.checkout.get_checkout_error_message.error_payment');
  }

  const message = getApiErrorMessage(err.response?.data);

  switch (message) {
    case 'Booking is no longer replaceable':
    case 'Only pending checkout bookings can be replaced':
    case 'Booking is not in PENDING state':
    case 'Payment is no longer available':
      return translate('pages.public.checkout.get_checkout_error_message.booking_payment_slot');
    case 'This slot is fully booked':
      return translate('pages.public.checkout.get_checkout_error_message.slot_select');
    case 'Cannot book a slot in the past':
      return translate('pages.public.checkout.get_checkout_error_message.slot_select_2');
    case 'Offer code not found':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code');
    case 'You cannot use your own offer code':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_2');
    case 'A referral offer is already linked to this account':
      return translate('pages.public.checkout.get_checkout_error_message.referral');
    case 'Promo code is inactive':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_inactive');
    case 'Promo code is not active yet':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_active_2');
    case 'Promo code has expired':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_3');
    case 'Promo code is not valid for this gym':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_4');
    case 'Promo code has already been used':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_5');
    case 'Promo code has already been used by this customer':
      return translate('pages.public.checkout.get_checkout_error_message.promo_code_6');
    case 'Promo code is valid only for the first booking':
      return translate('pages.public.checkout.get_checkout_error_message.booking_promo_code');
    case 'Payment amount is below the minimum card charge':
      return translate('pages.public.checkout.get_checkout_error_message.booking_payment_slot_2');
    case 'Tenant has not connected a Stripe account':
    case 'Tenant Stripe account is not ready to accept payments':
      return translate('pages.public.checkout.get_checkout_error_message.payment_gym');
    default:
      return (
        message || translate('pages.public.checkout.get_checkout_error_message.error_payment_2')
      );
  }
}

function isPasswordRequiredError(err: unknown) {
  if (!axios.isAxiosError(err)) return false;
  const details = (err.response?.data as { error?: { details?: { reason?: unknown } } } | undefined)
    ?.error?.details;
  return details?.reason === 'PASSWORD_REQUIRED';
}

function getApiErrorMessage(data: unknown) {
  if (!data || typeof data !== 'object') return null;
  const error = (data as { error?: unknown }).error;
  if (!error || typeof error !== 'object') return null;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : null;
}

function estimateApplicableCreditSplit(
  credits: CustomerCreditDashboard | null,
  tenantId: string | null,
  amountInSmallestUnit: number,
  currency: string,
) {
  const empty = {
    franchise: 0,
    global: 0,
    total: 0,
    franchiseName: translate('pages.public.checkout.empty.franchise_name'),
  };
  if (!credits || !tenantId || amountInSmallestUnit <= 0) return empty;

  const franchiseBalance = credits.balances.find(
    (balance) => balance.tenantId === tenantId && balance.currency === currency,
  );
  const franchiseCredit = franchiseBalance?.balanceInSmallestUnit ?? 0;
  const globalCredit =
    credits.balances.find((balance) => balance.tenantId === null && balance.currency === currency)
      ?.balanceInSmallestUnit ?? 0;
  const franchise = Math.min(franchiseCredit, amountInSmallestUnit);
  const global = Math.min(globalCredit, amountInSmallestUnit - franchise);

  return {
    franchise,
    global,
    total: franchise + global,
    franchiseName:
      franchiseBalance?.tenantName ??
      translate('pages.public.checkout.estimate_applicable_credit_split.franchise_name'),
  };
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        marginBottom: 8,
      }}
    >
      <span style={{ color: 'var(--af-muted)', fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: 14, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
